import React, { useCallback, useRef, useState } from 'react';
import {
  Bars3BottomLeftIcon,
  CloudArrowUpIcon,
  InformationCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import MultipleChoice from './Options/MultipleChoice';
import Select from '../../../../../../components/forms/Select/Select';
import ContentFooter from './ContentFooter';
import {
  patchFormContent,
  postRemoveContentImage,
} from '../../../../../../services/forms/formContentService';
import Checkboxes from './Options/Checkboxes';
import CheckboxIcon from '../../../../../../components/Icons/CheckboxIcon';
import MultipleChoiceIcon from '../../../../../../components/Icons/MultipleChoiceIcon';
import UploadImageModal from './UploadImageModal';
import SimpleLoader from '../../../../../../components/Loader/SimpleLoader';
import MiniRichTextEditor from '../../../../../../components/forms/Input/MiniRichTextEditor';
import TextAnswer from './Options/TextAnswer';
import FileAnswer from './Options/FileAnswer';
import { Button } from '../../../../../../components/forms/Buttons/Button';
import { toast } from 'react-toastify';
import GripIcon from '../../../../../../components/Icons/GripIcon';
import IntegerAnswer from './Options/IntegerAnswer';
import DropdownOption from './Options/DropdownOption';
import { useFormEdit } from '../../FormEditContext';
import ContentCondition from './ContentCondition';

export default function ContentCard({
  content,
  contents,
  selected,
  selectContent,
  setContents,
  update,
  setPoints,
  dragProps,
  isDragging,
  draggingId,
}) {
  const timer = useRef();
  const ref = useRef();
  const [imageUploading, setImageUploading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [conditionOpen, setConditionOpen] = useState(
    !!content.show_if?.length,
  );
  const { readOnly, reportError } = useFormEdit();
  // Пока перетаскивают любой вопрос, карточка свёрнута до заголовка: высокая
  // карточка (длинный список вариантов) иначе закрывает экран, а список
  // раздвигается на всю её высоту. Скрываем через CSS, а не размонтируем —
  // у вариантов и редактора своё несохранённое состояние.
  const collapsed = !!draggingId;

  const handleDeleteImage = () => {
    toast.dark(
      ({ closeToast }) => (
        <>
          <div>Удалить картинку из вопроса?</div>
          <div className='mt-3 flex justify-center gap-3'>
            <Button
              text={'Да'}
              color='error-solid'
              onClick={() => {
                closeToast();
                setImageUploading(true);
                postRemoveContentImage(content.uuid)
                  ?.then(() => {
                    update()?.finally(() => {
                      setImageUploading(false);
                    });
                  })
                  ?.catch((err) => {
                    setImageUploading(false);
                    reportError(err, 'Не удалось удалить картинку');
                  });
              }}
            />
            <Button text={'Нет'} className='text-white' onClick={closeToast} />
          </div>
        </>
      ),
      {
        toastId: `delete-image-${content.uuid}`,
        autoClose: false,
        closeButton: true,
      },
    );
  };

  const saveContent = useCallback(
    (value, field) => {
      patchFormContent(content.uuid, { [field]: `${value}` })
        ?.then((res) => {
          setTitle(res.name);
        })
        ?.catch((err) => reportError(err));
    },
    [content.uuid, reportError],
  );

  const debounceSaveData = useCallback(
    (value, field) => {
      // Отправить запрос через 1 секунду после последнего ввода
      if (timer.current) {
        clearTimeout(timer.current);
      }

      timer.current = setTimeout(() => {
        if (field === 'name' && value === '') {
          ref.current.innerHTML = title || content?.name;
          return;
        }
        saveContent(value, field);
        console.log(value);
      }, 1000);
    },
    [content?.name, saveContent, title],
  );

  return (
    <>
      <div
        id={content.uuid}
        onClick={selectContent}
        className={`group/card flex flex-col gap-2 rounded-lg border-l-[0.4rem] bg-white pr-6 pt-0
                   ${collapsed ? 'pb-3' : 'pb-6'}
                   ${selected ? 'border-l-blue-500' : 'border-l-white'} ${
          isDragging && 'opacity-50'
        }`}
      >
        <div className='flex flex-col items-center pt-2'>
          <div
            {...dragProps}
            className={`${
              draggingId && draggingId !== content.uuid
                ? ''
                : 'group-hover/card:visible'
            } ${!(isDragging || selected) && 'invisible'} text-secondary`}
          >
            <GripIcon className='absolute top-2 h-4 rotate-90' />
          </div>
        </div>
        {/* свёрнутая: до двух строк заголовка (дальше «…»), без панели
            форматирования. line-clamp в Tailwind 3.2 нет — свойства вручную */}
        <div
          className={`flex flex-row items-start pl-6 ${
            collapsed ? 'max-h-[4.75rem] overflow-hidden' : ''
          }`}
        >
          <MiniRichTextEditor
            ref={ref}
            borderColor='text-violet-500'
            className={`${selected && 'border-b'} w-full focus:outline-none ${
              // pb-0: overflow режет по краю padding, и в нижний отступ
              // выглядывал верх третьей строки
              collapsed
                ? 'overflow-hidden pb-0 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]'
                : ''
            }`}
            contentEditable={!readOnly}
            defaultValue={content.name}
            onChange={(e) => {
              debounceSaveData(e, 'name');
            }}
          />
          {content.is_required && <span className='text-error font-bold'>*</span>}
          <div className='flex flex-row items-center gap-2'>
            {selected && !collapsed && (
              <>
                {imageUploading ? (
                  <div className='relative'>
                    <SimpleLoader className='h-5' />
                  </div>
                ) : (
                  <UploadImageModal
                    uuid={content.uuid}
                    update={update}
                    setUploading={setImageUploading}
                  />
                )}
                {contentLoading ? (
                  <div className='relative'>
                    <SimpleLoader className='h-5' />
                  </div>
                ) : (
                  <Select
                    className='w-40'
                    value={content.test_type}
                    options={[
                      {
                        label: 'Multiple choice',
                        value: 'SELECTONE',
                        Icon: MultipleChoiceIcon,
                      },
                      {
                        label: 'Checkboxes',
                        value: 'SELECT',
                        Icon: CheckboxIcon,
                      },
                      {
                        label: 'Text answer',
                        value: 'TEXT',
                        Icon: Bars3BottomLeftIcon,
                      },
                      {
                        label: 'File answer',
                        value: 'FILE',
                        Icon: CloudArrowUpIcon,
                      },
                      {
                        label: 'Content',
                        value: 'CONTENT',
                        Icon: InformationCircleIcon,
                      },
                      {
                        label: 'Dropdown',
                        value: 'DROPDOWN',
                        Icon: InformationCircleIcon,
                      },
                      {
                        label: 'Number',
                        value: 'INTEGER',
                        Icon: InformationCircleIcon,
                      },
                    ]}
                    onChange={(e) => {
                      setContentLoading(true);

                      patchFormContent(content.uuid, {
                        test_type: e?.value,
                      })
                        ?.then(() => {
                          update()?.finally(() => {
                            setContentLoading(false);
                          });
                        })
                        ?.catch((err) => {
                          setContentLoading(false);
                          reportError(err);
                        });
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {/* contents: без свёртки обёртка не влияет на раскладку карточки */}
        <div className={collapsed ? 'hidden' : 'contents'}>
          {imageUploading && (
            <center>
              <SimpleLoader className='h-5' />
            </center>
          )}
          {content.attachment_img && !imageUploading && (
            <div className='group/image relative my-2 w-fit'>
              <img
                alt='attachment'
                className=': h-fit max-h-64 w-fit'
                src={content.attachment_img}
              />
              {selected && (
                <TrashIcon
                  className='invisible absolute -top-3 -right-3 h-7
              cursor-pointer rounded-full bg-slate-200 p-1.5 text-error group-hover/image:visible'
                  onClick={() => handleDeleteImage()}
                />
              )}
            </div>
          )}
          {contentLoading ? (
            <center>
              <SimpleLoader className='h-5' />
            </center>
          ) : (
            <>
              {['SELECTONE']?.includes(content.test_type) && (
                <MultipleChoice
                  content={content}
                  selected={selected}
                  update={update}
                />
              )}
              {['DROPDOWN']?.includes(content.test_type) && (
                <DropdownOption
                  content={content}
                  selected={selected}
                  update={update}
                />
              )}
              {content.test_type === 'SELECT' && (
                <Checkboxes
                  content={content}
                  selected={selected}
                  update={update}
                />
              )}
              {content.test_type === 'INTEGER' && <IntegerAnswer />}
              {content.test_type === 'TEXT' && <TextAnswer />}
              {content.test_type === 'FILE' && <FileAnswer />}
              <ContentCondition
                content={content}
                contents={contents}
                selected={selected}
                setContents={setContents}
                open={conditionOpen}
                setOpen={setConditionOpen}
              />
              <div
                className={`${
                  selected ? 'flex' : 'hidden'
                } ml-6 flex-row border-b pt-4`}
              />

              <ContentFooter
                setPoints={setPoints}
                content={content}
                selected={selected}
                setContents={setContents}
                onAddCondition={() => setConditionOpen(true)}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
